import React, { useState, useEffect } from "react";
import CloseIcon from "~/assets/svgs/close.svg";
import classNames from "classnames";
import useClient from "~/hooks/useClient";
import { Identifiers } from "~/constants/enum";
import useCmsBlocks from "~/hooks/useCmsBlocks";

type Props = {};

const BottomAlert = (props: Props) => {
  const isClient = useClient();

  const [open, setOpen] = useState(true);
  const [shouldDisplay, setShouldDisplay] = useState(false);
  const [closeBtnColor, setCloseBtnColor] = useState("white");

  const { cmsBlocks, loading: loadingCmsBlock } = useCmsBlocks([
    Identifiers.BottomAlert,
  ]);

  useEffect(() => {
    if (!isClient || loadingCmsBlock) return;

    const updateCountdown = () => {
      const bar = document.getElementById("event-notibar");
      if (!bar) return;

      const startDate = bar.dataset.start;
      const endDate = bar.dataset.end;
      const beforeStartText = bar.dataset.beforeStartText || "Event starts in";
      const afterStartText = bar.dataset.afterStartText || "Event ends in";
      const btnColor = bar.dataset.closeBtnColor || "white";

      // Update close button color
      setCloseBtnColor(btnColor);

      if (!startDate || !endDate) return;

      const start = new Date(startDate);
      const end = new Date(endDate);
      const now = new Date();

      // Hide component if event has ended
      if (now > end) {
        setShouldDisplay(false);
        return;
      }

      // Show component if event is valid (not ended)
      setShouldDisplay(true);

      const titleEl = document.getElementById("event-notibar-title");
      const daysEl = document.getElementById("event-notibar-days");
      const hoursEl = document.getElementById("event-notibar-hours");
      const minutesEl = document.getElementById("event-notibar-minutes");
      const secondsEl = document.getElementById("event-notibar-seconds");

      if (!titleEl || !daysEl || !hoursEl || !minutesEl || !secondsEl) return;

      let targetDate: Date;
      let titleText: string;

      // Event hasn't started yet
      if (now < start) {
        targetDate = start;
        titleText = beforeStartText;
      }
      // Event is currently running
      else {
        targetDate = end;
        titleText = afterStartText;
      }

      titleEl.textContent = titleText;

      // Calculate time difference
      const diff = targetDate.getTime() - now.getTime();

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      daysEl.textContent = `${days}d`;
      hoursEl.textContent = `${hours}h`;
      minutesEl.textContent = `${minutes}m`;
      secondsEl.textContent = `${seconds}s`;
    };

    // Update immediately
    updateCountdown();

    // Update every second
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [isClient, loadingCmsBlock]);

  if (loadingCmsBlock || !isClient) {
    return null;
  }

  const content = cmsBlocks?.[Identifiers.BottomAlert]?.content;

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div
      className={classNames(
        "relative max-h-0 overflow-hidden transition-all ease-in-out duration-200 ",
        {
          "max-h-40": !!content && shouldDisplay && open,
        }
      )}
    >
      <div className="">
        <div
          className="content text-center"
          dangerouslySetInnerHTML={{
            __html: content || "",
          }}
        />
      </div>
      <div className="absolute right-0 lg:right-4 top-0 flex items-center h-full">
        <span
          className="px-0 py-2 lg:p-2 rounded-sm hover:rounded-full hover:cursor-pointer"
          onClick={handleClose}
        >
          <CloseIcon
            width={24}
            height={24}
            className="transition-colors"
            style={{ color: closeBtnColor }}
          />
        </span>
      </div>
    </div>
  );
};

export default BottomAlert;
